import styled from '@grebban/style-system-react';
import useAppSelector from '@sportson/core-web/hooks/useAppSelector';
import { above } from '@sportson/core-web/utils/mediaqueries';
import BrandDescription from 'components/Brand/BrandDescription';
import BrandsList from 'components/Brand/BrandsList';
import Breadcrumbs from 'components/Breadcrumbs';
import { SearchInput } from 'components/SearchInput';
import Text from 'components/Text';
import LoadingContainer from 'containers/LoadingContainer';
import PostsContainer from '@sportson/core-web/containers/PostsContainer';
import React, { createRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

const Div = styled('div')();
const Wrapper = styled('div')`
    display: flex;
    flex-direction: column;
`;

const HeadingWrapper = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 32px 12px;

    ${above['desktop.sm']} {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        gap: 24px;

        padding: 24px 40px 40px;
    }
`;

const Title = styled(Text)();
interface BrandsCollectionProps {
    response: any;
}

function sortByFirstChar(arr) {
    return arr.sort((a, b) => {
        if (a.title.charAt(0).toLowerCase() < b.title.charAt(0).toLowerCase()) {
            return -1;
        }
        if (a.title.charAt(0).toLowerCase() > b.title.charAt(0).toLowerCase()) {
            return 1;
        }
        return 0;
    });
}

const Brands: React.FC<BrandsCollectionProps> = ({ response }) => {
    const { posts } = response;
    const brands = posts.filter((h) => h.template === 'default');

    const { t } = useTranslation();

    const [shownBrands, setShownBrands] = useState(brands);
    const pageData = useAppSelector(({ page }) => page);
    const { title } = pageData.data || {};
    const breadcrumbData = [{ permalink: null, title: pageData.data?.title }];

    const alphabet = sortByFirstChar(shownBrands).reduce(
        (acc, brand) => {
            if (!acc.includes(brand.title.charAt(0)) && isNaN(brand.title.charAt(0))) {
                acc.push(brand.title.charAt(0));
            }
            return acc;
        },
        ['#'],
    );

    const lettersWithBrands = alphabet.map((letter) => {
        const item: any = {
            letter,
        };

        if (shownBrands) {
            item.brands = shownBrands.filter((brand) => {
                const firstLetter = brand.title.charAt(0).toUpperCase();

                return !!(
                    firstLetter === item.letter ||
                    (item.letter === '#' && (parseInt(firstLetter, 10) || parseInt(firstLetter, 10) === 0))
                );
            });
        }

        return item;
    });

    if (lettersWithBrands.length < 1) {
        return null;
    }

    const handleFilterStores = useCallback((searchTerm) => {
        const brandsToShow = brands.filter((s) => s.title.toLowerCase().includes(searchTerm.toLowerCase()));
        setShownBrands(brandsToShow);
    }, []);

    return (
        <Wrapper>
            <Breadcrumbs
                gridColumn="standard"
                p={['16px 24px 16px 12px', null, null, null, null, '16px 40px']}
                breadcrumbs={breadcrumbData}
            />
            <Div>
                <HeadingWrapper>
                    {title && (
                        <Title
                            as="h1"
                            lineHeight="100%"
                            typography={['Content/32_120_0_500', null, null, null, null, 'Content/80_120_0_500']}
                        >
                            {title}
                        </Title>
                    )}
                    <BrandDescription />
                </HeadingWrapper>
                <Div padding={['24px 12px', null, null, null, null, '40px']}>
                    <SearchInput
                        width={[null, null, null, null, null, '304px']}
                        isLight
                        inputPlaceholder={t('brand.search_placeholder', { count: brands.length })}
                        setValue={handleFilterStores}
                    />
                </Div>
            </Div>
            <BrandsList lettersWithBrands={lettersWithBrands} />
        </Wrapper>
    );
};

const Loading = (props) => <LoadingContainer {...props} success={Brands} />;

const BrandsCollection = () => {
    const { t } = useTranslation();

    // @ts-expect-error: Reason:  This old js-component from years ago is not typed
    return <PostsContainer postType="storm_brand" pageSize={200} render={Loading} renderProps={{ t }} />;
};

export default BrandsCollection;
